import { Pencil, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { COARSE_HIT_24 } from '@/lib/touch-target';

/**
 * RowActions — the x-small pencil and trash every editable record row carries
 * (Health declutter, 2026-09-09; platform-wide 2026-09-10, DECISIONS).
 *
 * The detail dialog kept edit and delete one click too far away, which read as
 * "these cards can't be managed". The trash only ASKS — the page opens its
 * in-app `ConfirmActionDialog` (D-C, never browser confirm()) and runs the same
 * delete mutation the dialog does. Render a pencil only where an update
 * endpoint exists, and a trash only where the record is the reader's to delete.
 *
 * #2420 (D-2420 option A): the drawn button stays 24px (h-6 w-6) on every
 * pointer, so the box, hover fill and focus ring never change size. On a
 * coarse pointer (touch) an invisible ::after reaches 10px past each edge,
 * giving a 44px hit area that takes no layout space (`COARSE_HIT_24`, the
 * shared string in `lib/touch-target.ts`, #3167). The pseudo-element sits
 * at -z-10 inside the `isolate` wrapper, behind both drawn boxes. On a coarse
 * pointer the two boxes sit 20px apart (twice the 10px reach), so the two
 * 44px hit areas meet edge to edge and neither takes part of the other; with
 * the desktop 2px gap they overlapped and the pencil got only ~34px of width.
 */
export interface RowActionsProps {
  /**
   * The WHOLE accessible name of the pencil ("Edit task"), already in the
   * reader's language (#2109 R3 L0). The old `noun` + verb concatenation is
   * gone: word order and gender differ per language, so the caller passes one
   * key's text. Defaults to `common:actions.edit`.
   */
  editLabel?: string;
  /**
   * The whole name of the trash ("Delete medication"). A record that is
   * withdrawn rather than erased says so ("Cancel task"), so the button and
   * the confirm it opens use one word. Defaults to `common:actions.delete`.
   */
  deleteLabel?: string;
  onEdit?: () => void;
  onDelete?: () => void;
  className?: string;
}

export function RowActions({ editLabel, deleteLabel, onEdit, onDelete, className }: RowActionsProps) {
  const { t } = useTranslation('common');
  if (!onEdit && !onDelete) return null;
  return (
    <div className={cn('isolate flex items-center gap-0.5 [@media(pointer:coarse)]:gap-5', className)}>
      {onEdit && (
        <button
          type="button"
          aria-label={editLabel ?? t('actions.edit')}
          title={editLabel ?? t('actions.edit')}
          onClick={onEdit}
          className={`${COARSE_HIT_24} inline-flex h-6 w-6 items-center justify-center rounded-md text-stevens-ink-3 hover:bg-muted hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stevens-indigo/40`}
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
      )}
      {onDelete && (
        <button
          type="button"
          aria-label={deleteLabel ?? t('actions.delete')}
          title={deleteLabel ?? t('actions.delete')}
          onClick={onDelete}
          className={`${COARSE_HIT_24} inline-flex h-6 w-6 items-center justify-center rounded-md text-stevens-ink-3 hover:bg-muted hover:text-status-danger-fg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stevens-indigo/40`}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
